/**
 * Authentication service for JWT and API Key management
 */

import { syncContactsFromServer, saveContactsToFile } from './userContactsService';

const AUTH_API_URL = 'https://apicabaza.creago.be/auth.cfc';
const STORAGE_KEY_TOKEN = 'cabaza_jwt_token';
const STORAGE_KEY_API_KEY = 'cabaza_api_key';
const STORAGE_KEY_USER = 'cabaza_user';

/**
 * Login with username and password to get JWT token
 * @param {string} username
 * @param {string} password
 * @returns {Promise<Object>} Login result with token
 */
export const login = async (username, password) => {
    try {
        const response = await fetch(`${AUTH_API_URL}?method=login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ username, password })
        });

        const result = await response.json();

        if (result.success && result.token) {
            // Store token and user info
            localStorage.setItem(STORAGE_KEY_TOKEN, result.token);
            localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(result.user));

            // Sync contacts from server (async, don't block login)
            const user = result.user;
            const userId = user.id || user.ID;
            const peopleuuid = user.peopleUuid || user.peopleuuid || user.PEOPLEUUID;
            const storageKey = user.storageKey || user.STORAGEKEY;

            if (userId && peopleuuid && storageKey) {
                syncContactsFromServer(userId, peopleuuid, storageKey)
                    .then(() => console.log('Contacts synced from server after login'))
                    .catch(err => console.error('Error syncing contacts after login:', err));
            }

            return result;
        }

        throw new Error(result.message || 'Login failed');
    } catch (error) {
        console.error('Login error:', error);
        throw error;
    }
};

/**
 * Get API key with username and password
 * @param {string} username
 * @param {string} password
 * @returns {Promise<Object>} API key result
 */
export const getApiKey = async (username, password) => {
    try {
        const response = await fetch(`${AUTH_API_URL}?method=getApiKey`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ username, password })
        });

        const result = await response.json();

        if (result.success && result.apiKey) {
            // Store API key
            localStorage.setItem(STORAGE_KEY_API_KEY, result.apiKey);
            return result;
        }

        throw new Error(result.message || 'Failed to get API key');
    } catch (error) {
        console.error('Get API key error:', error);
        throw error;
    }
};

/**
 * Logout - save contacts to server then clear stored auth data
 */
export const logout = async () => {
    try {
        // Get user info before clearing
        const userJson = localStorage.getItem(STORAGE_KEY_USER);
        if (userJson) {
            const user = JSON.parse(userJson);
            const userId = user.id || user.ID;
            const peopleuuid = user.peopleUuid || user.peopleuuid || user.PEOPLEUUID;
            const storageKey = user.storageKey || user.STORAGEKEY;

            // Get contacts from localStorage
            const contactsData = localStorage.getItem('cabaza_user_contacts');
            if (contactsData && peopleuuid && storageKey) {
                const allContacts = JSON.parse(contactsData);
                const userContacts = allContacts[userId] || [];

                if (userContacts.length > 0) {
                    console.log('Saving contacts to server before logout...', userContacts.length, 'contacts');
                    await saveContactsToFile(peopleuuid, storageKey, userContacts);
                    console.log('Contacts saved to server successfully');
                }
            }
        }
    } catch (error) {
        console.error('Error saving contacts on logout:', error);
    }

    // Clear all auth data
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_KEY_USER);
    localStorage.removeItem(STORAGE_KEY_API_KEY);
};

/**
 * Get stored JWT token
 * @returns {string|null} JWT token or null
 */
export const getToken = () => {
    return localStorage.getItem(STORAGE_KEY_TOKEN);
};

/**
 * Get stored API key
 * @returns {string|null} API key or null
 */
export const getStoredApiKey = () => {
    return localStorage.getItem(STORAGE_KEY_API_KEY);
};

/**
 * Get stored user info
 * @returns {Object|null} User object or null
 */
export const getUser = () => {
    const userJson = localStorage.getItem(STORAGE_KEY_USER);
    return userJson ? JSON.parse(userJson) : null;
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = () => {
    return !!(getToken() || getStoredApiKey());
};

// Default API key for fallback
const DEFAULT_API_KEY = 'cabaza_api_key_2024_change_me';

/**
 * Get authorization headers for API requests
 * @returns {Object} Headers object with authorization
 */
export const getAuthHeaders = () => {
    const token = getToken();
    const apiKey = getStoredApiKey();

    const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    };

    // Prefer JWT token if available, then stored API key, then default
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    } else if (apiKey) {
        headers['X-API-Key'] = apiKey;
    } else {
        // Fallback to default API key
        headers['X-API-Key'] = DEFAULT_API_KEY;
    }

    return headers;
};

/**
 * Auto-login with hardcoded API key for demo purposes
 * In production, implement proper login flow
 */
export const autoLoginWithApiKey = () => {
    const apiKey = 'cabaza_api_key_2024_change_me';
    localStorage.setItem(STORAGE_KEY_API_KEY, apiKey);
    console.log('Auto-logged in with API key');
};
